import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import Layout from '../components/Layout';
import { GET_BOOST_PACKAGES } from '../graphql/queries';
import { Card, Button, Row, Col, Badge } from 'react-bootstrap';
import { CheckCircleFill, StarFill, LightningFill, GraphUpArrow } from 'react-bootstrap-icons';

const Pricing: React.FC = () => {
  const [selectedBoost, setSelectedBoost] = useState<string | null>(null);

  const { data: packagesData } = useQuery(GET_BOOST_PACKAGES);

  const plans = [
    {
      name: 'Basic',
      price: 'Free',
      description: 'Perfect for casual sellers',
      features: [
        'Up to 10 active listings',
        'Standard listing placement',
        'Photo uploads (up to 5 per ad)',
        'Basic analytics',
        'Email support',
      ],
      cta: 'Get Started',
      popular: false,
    },
    {
      name: 'Premium',
      price: 'Rs. 999',
      period: '/month',
      description: 'For serious sellers',
      features: [
        'Up to 50 active listings',
        'Priority listing placement',
        'Photo uploads (up to 10 per ad)',
        'Advanced analytics',
        'Priority support',
        'Featured ad discounts',
        'Verified seller badge',
      ],
      cta: 'Upgrade Now',
      popular: true,
    },
    {
      name: 'Business',
      price: 'Rs. 2,999',
      period: '/month',
      description: 'For businesses & dealers',
      features: [
        'Unlimited active listings',
        'Top placement in search',
        'Photo uploads (up to 20 per ad)',
        'Full analytics dashboard',
        '24/7 phone support',
        'Free featured ads (5/month)',
        'Verified business badge',
        'API access',
      ],
      cta: 'Contact Sales',
      popular: false,
    },
  ];

  const boostPackages = packagesData?.boostPackages || [];

  // Map package type to icon and color
  const getPackageIcon = (type: string) => {
    switch(type) {
      case 'featured': return StarFill;
      case 'trending': return GraphUpArrow;
      case 'both': return LightningFill;
      default: return StarFill;
    }
  };

  const getPackageColor = (type: string) => {
    switch(type) {
      case 'featured': return 'primary';
      case 'trending': return 'purple';
      case 'both': return 'success';
      default: return 'primary';
    }
  };

  // Static boost packages fallback (will be replaced by API data)
  const staticBoostPackages = [
    {
      id: 'basic',
      name: 'Basic Boost',
      duration: '3 days',
      duration_days: 3,
      price: 300,
      features: [
        'Appears in "Featured" section',
        'Highlighted in search results',
        '2x more views on average',
        'Basic analytics'
      ],
      icon: StarFill,
      color: 'primary'
    },
    {
      id: 'standard',
      name: 'Standard Boost',
      duration: '7 days',
      duration_days: 7,
      price: 500,
      features: [
        'Top of category page',
        'Prominent badge display',
        '5x more views on average',
        'Priority support',
        'Advanced analytics'
      ],
      icon: LightningFill,
      color: 'success',
      popular: true
    },
    {
      id: 'premium',
      name: 'Premium Boost',
      duration: '14 days',
      duration_days: 14,
      price: 900,
      features: [
        'Homepage featured spot',
        'Social media promotion',
        '10x more views on average',
        'Dedicated support',
        'Full analytics dashboard'
      ],
      icon: GraphUpArrow,
      color: 'purple'
    },
    {
      id: 'ultimate',
      name: 'Ultimate Boost',
      duration: '30 days',
      duration_days: 30,
      price: 1500,
      features: [
        'Maximum visibility',
        'All premium features',
        '15x more views on average',
        '24/7 priority support',
        'Custom analytics reports'
      ],
      icon: StarFill,
      color: 'warning'
    },
  ];

  const displayPackages = boostPackages.length > 0 ? boostPackages : staticBoostPackages;

  const handleSelectBoost = (packageId: string) => {
    setSelectedBoost(packageId);
    // Scroll to boost section or open modal
    console.log('Selected boost package:', packageId);
  };

  return (
    <Layout showSidebar={false}>
      <div className="container py-5">
        <div className="text-center mb-5">
          <h1 className="display-5 fw-bold mb-3">Simple, Transparent Pricing</h1>
          <p className="lead text-muted mx-auto" style={{ maxWidth: '600px' }}>
            Choose the plan that works best for you. All plans include our core features
            to help you buy and sell with confidence.
          </p>
        </div>

        {/* Pricing Cards */}
        <Row className="g-4 mb-5">
          {plans.map((plan) => (
            <Col md={4} key={plan.name}>
              <Card
                className={`h-100 ${
                  plan.popular
                    ? 'border-primary shadow-lg position-relative'
                    : 'shadow-sm'
                }`}
              >
                {plan.popular && (
                  <Badge
                    bg="white"
                    text="primary"
                    className="position-absolute top-0 start-50 translate-middle mt-2 px-3 py-2 fw-semibold"
                  >
                    Most Popular
                  </Badge>
                )}
                <Card.Body
                  className={`d-flex flex-column ${
                    plan.popular ? 'bg-primary bg-opacity-10' : ''
                  }`}
                >
                  <Card.Title as="h3" className={`fw-bold ${plan.popular ? '' : 'text-dark'}`}>
                    {plan.name}
                  </Card.Title>
                  <Card.Text className={plan.popular ? 'text-primary-emphasis' : 'text-muted'}>
                    {plan.description}
                  </Card.Text>
                  <div className="mb-4">
                    <span className={`display-6 fw-bold ${plan.popular ? 'text-primary' : 'text-dark'}`}>
                      {plan.price}
                    </span>
                    {plan.period && (
                      <span className={plan.popular ? 'text-primary-emphasis' : 'text-muted'}>
                        {plan.period}
                      </span>
                    )}
                  </div>
                  <ul className="list-unstyled mb-4 flex-grow-1">
                    {plan.features.map((feature) => (
                      <li key={feature} className="d-flex align-items-start mb-3">
                        <CheckCircleFill
                          className={`me-2 flex-shrink-0 mt-1 ${
                            plan.popular ? 'text-primary' : 'text-success'
                          }`}
                        />
                        <span className={plan.popular ? 'text-primary-emphasis' : 'text-secondary'}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant={plan.popular ? 'light' : 'primary'}
                    className="w-100 fw-semibold"
                  >
                    {plan.cta}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Boost Packages */}
        <Card className="mb-5 bg-light border-0">
          <Card.Body className="p-4 p-md-5">
            <div className="text-center mb-5">
              <div className="d-inline-flex align-items-center gap-2 bg-primary bg-opacity-10 text-primary px-4 py-2 rounded-pill fw-semibold mb-3">
                <LightningFill />
                Boost Your Sales
              </div>
              <h2 className="fw-bold mb-3">Featured Ad Boosts</h2>
              <p className="text-muted mx-auto" style={{ maxWidth: '600px' }}>
                Want to get more visibility for your listings? Boost your ads to appear
                at the top of search results and category pages.
              </p>
            </div>

            <Row className="g-3">
              {displayPackages.map((pkg: any) => {
                const IconComponent = pkg.icon || getPackageIcon(pkg.packageType);
                const color = pkg.color || getPackageColor(pkg.packageType);
                const isSelected = selectedBoost === pkg.id;
                const price = pkg.price ? parseFloat(pkg.price) : pkg.price;

                return (
                  <Col xs={12} sm={6} lg={3} key={pkg.id}>
                    <Card
                      className={`h-100 text-center shadow-sm transition-all ${
                        isSelected ? 'border-primary border-2 shadow' : ''
                      } ${pkg.popular ? 'position-relative' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSelectBoost(pkg.id)}
                    >
                      {pkg.popular && (
                        <Badge
                          bg="success"
                          className="position-absolute top-0 start-50 translate-middle mt-2 px-3 py-1 fw-semibold small"
                        >
                          Best Value
                        </Badge>
                      )}
                      <Card.Body className="d-flex flex-column">
                        <div
                          className={`mx-auto mb-3 rounded-circle d-flex align-items-center justify-content-center bg-${color} bg-opacity-10`}
                          style={{ width: '56px', height: '56px' }}
                        >
                          <IconComponent className={`text-${color}`} style={{ width: '28px', height: '28px' }} />
                        </div>
                        <Card.Title as="h5" className="fw-bold mb-1">
                          {pkg.name}
                        </Card.Title>
                        <Card.Text className="small text-muted mb-2">
                          {pkg.duration || `${pkg.durationDays} days`}
                        </Card.Text>
                        <div className="mb-3">
                          <span className="fw-bold">
                            Rs {typeof price === 'number' ? price.toLocaleString() : price}
                          </span>
                        </div>
                        <ul className="list-unstyled text-start small text-secondary mb-3 flex-grow-1">
                          {(pkg.features || (typeof pkg.features === 'string' ? JSON.parse(pkg.features || '{}') : [])).length > 0
                            ? (typeof pkg.features === 'string'
                                ? Object.values(JSON.parse(pkg.features || '{}'))
                                : pkg.features
                              ).map((feature: any, idx: number) => (
                                <li key={idx} className="d-flex align-items-start mb-2">
                                  <CheckCircleFill className="text-success me-2 flex-shrink-0 mt-1 small" />
                                  <span>{typeof feature === 'string' ? feature : feature.name || feature}</span>
                                </li>
                              ))
                            : staticBoostPackages.find(p => p.id === pkg.id)?.features.map((feature: string) => (
                                <li key={feature} className="d-flex align-items-start mb-2">
                                  <CheckCircleFill className="text-success me-2 flex-shrink-0 mt-1 small" />
                                  <span>{feature}</span>
                                </li>
                              ))
                          }
                        </ul>
                        <Button
                          variant={isSelected ? 'primary' : 'outline-secondary'}
                          className="w-100 fw-semibold"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectBoost(pkg.id);
                          }}
                        >
                          {isSelected ? 'Selected' : 'Select Package'}
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>

            {selectedBoost && (
              <Card className="mt-4 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <div>
                      <h5 className="fw-bold mb-1">Ready to boost your ad?</h5>
                      <p className="small text-muted mb-0">
                        Selected: {displayPackages.find((p: any) => p.id === selectedBoost)?.name}
                      </p>
                    </div>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-secondary"
                        onClick={() => setSelectedBoost(null)}
                      >
                        Cancel
                      </Button>
                      <Link
                        to="/dashboard"
                        className="btn btn-primary fw-semibold"
                      >
                        Go to Dashboard
                      </Link>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            )}
          </Card.Body>
        </Card>

        {/* How It Works */}
        <div className="mb-5">
          <h2 className="fw-bold text-center mb-4">How Boosting Works</h2>
          <Row className="g-4">
            <Col xs={12} sm={6} md={3}>
              <div className="text-center">
                <div
                  className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center mx-auto mb-3"
                  style={{ width: '64px', height: '64px' }}
                >
                  <span className="fw-bold text-primary" style={{ fontSize: '1.5rem' }}>1</span>
                </div>
                <h5 className="fw-semibold mb-2">Choose Package</h5>
                <p className="small text-muted">Select a boost package that fits your budget and needs</p>
              </div>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <div className="text-center">
                <div
                  className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center mx-auto mb-3"
                  style={{ width: '64px', height: '64px' }}
                >
                  <span className="fw-bold text-primary" style={{ fontSize: '1.5rem' }}>2</span>
                </div>
                <h5 className="fw-semibold mb-2">Select Ad</h5>
                <p className="small text-muted">Choose which ad you want to boost from your dashboard</p>
              </div>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <div className="text-center">
                <div
                  className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center mx-auto mb-3"
                  style={{ width: '64px', height: '64px' }}
                >
                  <span className="fw-bold text-primary" style={{ fontSize: '1.5rem' }}>3</span>
                </div>
                <h5 className="fw-semibold mb-2">Make Payment</h5>
                <p className="small text-muted">Complete secure payment via eSewa, Khalti, or card</p>
              </div>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <div className="text-center">
                <div
                  className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center mx-auto mb-3"
                  style={{ width: '64px', height: '64px' }}
                >
                  <span className="fw-bold text-primary" style={{ fontSize: '1.5rem' }}>4</span>
                </div>
                <h5 className="fw-semibold mb-2">Get Results</h5>
                <p className="small text-muted">Watch your ad get more views and sell faster</p>
              </div>
            </Col>
          </Row>
        </div>

        {/* FAQ */}
        <div className="mt-5">
          <h2 className="fw-bold text-center mb-4">Frequently Asked Questions</h2>
          <Row className="g-4">
            <Col md={6}>
              <div className="mb-3">
                <h5 className="fw-semibold mb-2">Can I change my plan anytime?</h5>
                <p className="text-muted small">
                  Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.
                </p>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <h5 className="fw-semibold mb-2">Is there a free trial?</h5>
                <p className="text-muted small">
                  Yes, all new users start with the Basic plan which is completely free.
                </p>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <h5 className="fw-semibold mb-2">What payment methods do you accept?</h5>
                <p className="text-muted small">
                  We accept eSewa, Khalti, imePay, all major credit cards, and debit cards.
                </p>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <h5 className="fw-semibold mb-2">Can I get a refund?</h5>
                <p className="text-muted small">
                  We offer refunds within 7 days if you're not satisfied with your subscription.
                </p>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <h5 className="fw-semibold mb-2">How long does boost last?</h5>
                <p className="text-muted small">
                  Boost duration depends on the package you choose - from 3 days to 30 days.
                </p>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <h5 className="fw-semibold mb-2">Can I boost multiple ads?</h5>
                <p className="text-muted small">
                  Yes! You can boost as many ads as you want. Each boost is purchased separately.
                </p>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </Layout>
  );
};

export default Pricing;
